import { supabase } from '../lib/supabaseClient';
import type {
  DashboardPreference,
  MetricDefinition,
  ReportRun,
  ReportSnapshot,
  ReportType,
  UUID,
} from '../types/domain';

/**
 * Command Center & Reporting (FASE 12, migration 0050) — reads and writes
 * wired to the real backend. Every write below calls a permission-gated
 * `_checked` RPC — never a direct `.insert()`/`.update()`. All 4 tables carry
 * RESTRICTIVE `false` INSERT/UPDATE/DELETE RLS from the start (the FASE10/11
 * raw-bypass lesson applied proactively), so a direct write call would fail
 * even if attempted here.
 *
 * Consolidated (tenant-wide) requests: pass `branchId = null`. The backend
 * requires an actual tenant-wide role grant for this (OWNER/TENANT_OWNER) —
 * a branch-scoped MANAGER's call is rejected server-side, not merely hidden
 * in the UI.
 */

// ---------------------------------------------------------------------------
// Metric registry (KPI dictionary)
// ---------------------------------------------------------------------------

export async function listMetrics(): Promise<MetricDefinition[]> {
  const { data, error } = await supabase
    .from('metric_registry')
    .select(
      'metric_id, tenant_id, metric_code, version, name, description, unit, source_domain, formula_definition, status, effective_from, effective_to, created_at, created_by'
    )
    .order('source_domain', { ascending: true })
    .order('metric_code', { ascending: true });
  if (error) throw new Error(`listMetrics failed: ${error.message}`);
  return (data ?? []) as MetricDefinition[];
}

/** TENANT_OWNER-only server-side (metric.create) — formula integrity is deliberately not delegated to operational roles. */
export async function registerMetric(
  metricCode: string,
  name: string,
  description: string | null,
  unit: string,
  sourceDomain: string,
  formulaDefinition: Record<string, unknown>
): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_register_metric_checked', {
    p_metric_code: metricCode,
    p_name: name,
    p_description: description,
    p_unit: unit,
    p_source_domain: sourceDomain,
    p_formula_definition: formulaDefinition,
  });
  if (error) throw new Error(`fn_register_metric_checked failed: ${error.message}`);
  return data as UUID;
}

/**
 * SCD-Type-2 re-versioning (metric.update, TENANT_OWNER-only): marks the
 * current ACTIVE row SUPERSEDED and inserts version+1 as ACTIVE. Never
 * mutates a metric_registry row in place — every prior report_snapshot keeps
 * referencing its original metric_id/version, so historical figures remain
 * reproducible under the formula that actually produced them.
 */
export async function updateMetricDefinition(
  metricCode: string,
  newFormulaDefinition: Record<string, unknown>,
  newDescription: string | null
): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_update_metric_definition_checked', {
    p_metric_code: metricCode,
    p_new_formula_definition: newFormulaDefinition,
    p_new_description: newDescription,
  });
  if (error) throw new Error(`fn_update_metric_definition_checked failed: ${error.message}`);
  return data as UUID;
}

/**
 * Ad-hoc re-verification of a single metric (e.g. a Command Center card's
 * "recompute now" action) — separate from report_snapshots, so it carries
 * its own permission + tenant-wide-vs-branch + PAYROLL-masking checks
 * server-side (fn_compute_metric_value_checked), matching report_snapshots
 * RLS exactly.
 */
export async function computeMetricValue(
  metricId: UUID,
  periodStart: string,
  periodEnd: string,
  branchId: UUID | null
): Promise<{ computed_value: number; drill_down: Array<Record<string, unknown>> } | null> {
  const { data, error } = await supabase.rpc('fn_compute_metric_value_checked', {
    p_metric_id: metricId,
    p_period_start: periodStart,
    p_period_end: periodEnd,
    p_branch_id: branchId,
  });
  if (error) throw new Error(`fn_compute_metric_value_checked failed: ${error.message}`);
  const row = Array.isArray(data) ? data[0] : data;
  return row ?? null;
}

// ---------------------------------------------------------------------------
// Report generation
// ---------------------------------------------------------------------------

/**
 * branchId = null => consolidated/tenant-wide report — requires a
 * tenant-wide report.run grant server-side.
 *
 * idempotencyKey (migration 0051, closure-correction pass): generate ONCE
 * per logical "generate this report" action (e.g. once when the form is
 * submitted) and reuse the SAME key across a retry of that exact action
 * (double-click, network-retry) — the server deterministically resolves a
 * retry to the already-created canonical report_run (unique
 * `(tenant_id, idempotency_key)`), never a duplicate. A genuinely new,
 * intentional generate action (a fresh form submission) must always use a
 * fresh key — reusing an old key would silently return that OLD report_run
 * instead of creating a new one.
 */
export async function runReport(
  branchId: UUID | null,
  reportType: ReportType,
  periodStart: string,
  periodEnd: string,
  idempotencyKey: UUID
): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_run_report_checked', {
    p_branch_id: branchId,
    p_report_type: reportType,
    p_period_start: periodStart,
    p_period_end: periodEnd,
    p_idempotency_key: idempotencyKey,
  });
  if (error) throw new Error(`fn_run_report_checked failed: ${error.message}`);
  return data as UUID;
}

/** idempotencyKey: see the note on runReport above — same contract. */
export async function generateDailyReport(branchId: UUID | null, date: string, idempotencyKey: UUID): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_generate_daily_report_checked', {
    p_branch_id: branchId,
    p_date: date,
    p_idempotency_key: idempotencyKey,
  });
  if (error) throw new Error(`fn_generate_daily_report_checked failed: ${error.message}`);
  return data as UUID;
}

/** monthStart must be the first day of a month (server-enforced). idempotencyKey: see the note on runReport above. */
export async function generateMonthlyReport(branchId: UUID | null, monthStart: string, idempotencyKey: UUID): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_generate_monthly_report_checked', {
    p_branch_id: branchId,
    p_month_start: monthStart,
    p_idempotency_key: idempotencyKey,
  });
  if (error) throw new Error(`fn_generate_monthly_report_checked failed: ${error.message}`);
  return data as UUID;
}

/**
 * Tenant-wide only (loops every ACTIVE branch) — requires a tenant-wide
 * report.run grant, same as a null-branch runReport call. idempotencyKey is
 * ONE base key for the whole comparison request — the server derives a
 * deterministic per-branch key from it, so retrying the SAME comparison
 * request maps to the same set of per-branch canonical runs.
 */
export async function runBranchComparisonReport(
  reportType: ReportType,
  periodStart: string,
  periodEnd: string,
  idempotencyKey: UUID
): Promise<Array<{ branch_id: UUID; branch_code: string; report_run_id: UUID }>> {
  const { data, error } = await supabase.rpc('fn_run_branch_comparison_report_checked', {
    p_report_type: reportType,
    p_period_start: periodStart,
    p_period_end: periodEnd,
    p_idempotency_key: idempotencyKey,
  });
  if (error) throw new Error(`fn_run_branch_comparison_report_checked failed: ${error.message}`);
  return (data ?? []) as Array<{ branch_id: UUID; branch_code: string; report_run_id: UUID }>;
}

// ---------------------------------------------------------------------------
// Report runs & snapshots (read)
// ---------------------------------------------------------------------------

export async function listReportRuns(): Promise<ReportRun[]> {
  const { data, error } = await supabase
    .from('report_runs')
    .select(
      'report_run_id, tenant_id, branch_id, report_type, period_start, period_end, status, freshness_note, failure_reason, requested_by, requested_at, completed_at'
    )
    .order('requested_at', { ascending: false });
  if (error) throw new Error(`listReportRuns failed: ${error.message}`);
  return (data ?? []) as ReportRun[];
}

export async function getReportRun(reportRunId: UUID): Promise<ReportRun | null> {
  const { data, error } = await supabase
    .from('report_runs')
    .select(
      'report_run_id, tenant_id, branch_id, report_type, period_start, period_end, status, freshness_note, failure_reason, requested_by, requested_at, completed_at'
    )
    .eq('report_run_id', reportRunId)
    .maybeSingle();
  if (error) throw new Error(`getReportRun failed: ${error.message}`);
  return data as ReportRun | null;
}

/**
 * RLS-masked: a PAYROLL-domain row (e.g. PAYROLL_GROSS) is silently absent
 * from this result unless the caller also holds payroll_fact.read — the
 * report_run itself and every non-payroll metric row stay visible either
 * way. Never surfaced as an error; the row is simply not in the array.
 */
export async function listReportSnapshots(reportRunId: UUID): Promise<ReportSnapshot[]> {
  const { data, error } = await supabase
    .from('report_snapshots')
    .select('snapshot_id, seq, tenant_id, report_run_id, metric_id, metric_code, computed_value, drill_down, computed_at')
    .eq('report_run_id', reportRunId)
    .order('metric_code', { ascending: true });
  if (error) throw new Error(`listReportSnapshots failed: ${error.message}`);
  return (data ?? []) as ReportSnapshot[];
}

/** FINAL / CURRENT / STALE / N_A — never a browser-side toggle, always derived from real accounting_periods.status. */
export async function checkReportFreshness(reportRunId: UUID): Promise<string> {
  const { data, error } = await supabase.rpc('fn_check_report_freshness_checked', {
    p_report_run_id: reportRunId,
  });
  if (error) throw new Error(`fn_check_report_freshness_checked failed: ${error.message}`);
  return data as string;
}

export interface ReportAuditEvent {
  event_id: UUID;
  occurred_at: string;
  actor_user_id: UUID;
  action: string;
  after_json: Record<string, unknown>;
}

export async function getReportAuditTrail(reportRunId: UUID): Promise<ReportAuditEvent[]> {
  const { data, error } = await supabase.rpc('fn_get_report_audit_trail_checked', {
    p_report_run_id: reportRunId,
  });
  if (error) throw new Error(`fn_get_report_audit_trail_checked failed: ${error.message}`);
  return (data ?? []) as ReportAuditEvent[];
}

// ---------------------------------------------------------------------------
// Dashboard preferences (presentation only — never authorization/scope)
// ---------------------------------------------------------------------------

export async function getMyDashboardPreference(): Promise<DashboardPreference | null> {
  const { data, error } = await supabase
    .from('dashboard_preferences')
    .select('preference_id, tenant_id, user_id, pinned_metrics, layout, updated_at, updated_by')
    .maybeSingle();
  if (error) throw new Error(`getMyDashboardPreference failed: ${error.message}`);
  return data as DashboardPreference | null;
}

/** p_user_id is deliberately not a parameter server-side — always the caller's own resolved id. */
export async function setDashboardPreference(
  pinnedMetrics: string[],
  layout: Record<string, unknown>
): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_set_dashboard_preference_checked', {
    p_pinned_metrics: pinnedMetrics,
    p_layout: layout,
  });
  if (error) throw new Error(`fn_set_dashboard_preference_checked failed: ${error.message}`);
  return data as UUID;
}
